import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Phone, 
  Mail,
  CheckCircle2,
  Loader2,
  Car,
  User,
  FileText
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { DEALERSHIP_INFO } from '../config/dealership';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RepairsPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicle_details: '',
    issue_summary: '',
    notes: '',
    preferred_datetime: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Only name, email, phone are required
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Map to backend expected format
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        car_reg: formData.vehicle_details.trim() || 'N/A',
        make: 'N/A',
        model: 'N/A',
        year: null,
        issue_summary: formData.issue_summary.trim() || null,
        notes: formData.notes.trim() || null,
        preferred_datetime: formData.preferred_datetime.trim() || null
      };

      const response = await fetch(`${API}/repairs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit your request');
      }

      setIsSubmitted(true);
      toast.success('Your appointment request has been submitted!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const appointmentTypes = [
    { title: 'View a Vehicle', description: 'Come see a car in person before you buy' },
    { title: 'Test Drive', description: 'Take your chosen vehicle for a spin' },
    { title: 'General Enquiry', description: 'Discuss your requirements with our team' },
    { title: 'Full Servicing', description: 'Complete service packages for your car' },
    { title: 'NCT Prep', description: 'Professional vehicle preparation for NCT' },
    { title: 'Mechanical Work', description: 'Repairs, diagnostics, and maintenance' },
  ];

  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#020405] pt-20" data-testid="repairs-page">
        <section className="py-20 md:py-28">
          <div className="max-w-2xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-[#0A0F14] border border-[#48CCCD]/30 p-8 md:p-12 text-center"
              data-testid="repair-success-message"
            >
              <div className="w-20 h-20 mx-auto flex items-center justify-center bg-[#48CCCD]/20 border border-[#48CCCD]/50 mb-6">
                <CheckCircle2 size={40} className="text-[#48CCCD]" />
              </div>
              
              <h1 className="font-serif text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-4">
                Request Received!
              </h1>
              
              <p className="text-[#94A3B8] font-sans text-lg leading-relaxed mb-8">
                Thank you for your appointment request. Our team will contact you shortly to 
                confirm the details and arrange a convenient time.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 h-12 px-8 bg-[#48CCCD] text-black font-bold uppercase tracking-widest text-xs hover:bg-white transition-colors"
                >
                  Return Home
                </Link>
                <Link
                  to="/cars"
                  className="inline-flex items-center justify-center gap-2 h-12 px-8 bg-transparent border-2 border-white/30 text-white font-bold uppercase tracking-widest text-xs hover:border-[#48CCCD] hover:text-[#48CCCD] transition-colors"
                >
                  Browse Our Cars
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020405] pt-20" data-testid="repairs-page">
      {/* Hero Section */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#16202A] via-[#0A0F14] to-[#020405]" />
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#48CCCD]/5 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-[#48CCCD]/20">
                  <Calendar size={24} className="text-[#48CCCD]" />
                </div>
                <p className="text-[#48CCCD] font-sans text-xs uppercase tracking-[0.3em]">
                  Appointments
                </p>
              </div>
              
              <h1 
                className="font-serif text-4xl sm:text-5xl font-bold text-[#F8FAFC] leading-tight tracking-tight mb-6"
                data-testid="repairs-page-title"
              >
                Book an
                <span className="text-[#48CCCD]"> Appointment</span>
              </h1>
              
              <p className="font-sans text-lg text-[#94A3B8] leading-relaxed mb-8">
                Whether you want to view a car, arrange a test drive, or book in for servicing — 
                fill in the form and our team will contact you to arrange a convenient time.
              </p>
              
              {/* Appointment Types Grid */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {appointmentTypes.map((type, index) => (
                  <motion.div
                    key={type.title}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                    className="bg-[#16202A]/50 border border-white/10 p-4"
                  >
                    <h3 className="font-serif text-sm font-bold text-[#F8FAFC] mb-1">
                      {type.title}
                    </h3>
                    <p className="text-xs text-[#94A3B8] font-sans">
                      {type.description}
                    </p>
                  </motion.div>
                ))}
              </div>
              
              {/* Contact Info */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={DEALERSHIP_INFO.phoneHref}
                  className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-[#48CCCD] transition-colors text-sm"
                >
                  <Phone size={16} />
                  {DEALERSHIP_INFO.phone}
                </a>
                <a
                  href={DEALERSHIP_INFO.emailHref}
                  className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-[#48CCCD] transition-colors text-sm"
                >
                  <Mail size={16} />
                  {DEALERSHIP_INFO.email}
                </a>
              </div>
            </motion.div>
            
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <form 
                onSubmit={handleSubmit} 
                className="bg-[#0A0F14] border border-white/10 p-6 md:p-8"
                data-testid="repair-form"
              >
                <h2 className="font-serif text-xl font-bold text-[#F8FAFC] mb-6">
                  Request an Appointment
                </h2>
                
                {/* Your Details */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <User size={16} className="text-[#48CCCD]" />
                    <span className="text-[10px] uppercase tracking-[0.15em] text-[#48CCCD] font-sans font-medium">
                      Your Details
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your full name"
                        className={`bg-[#16202A] border-white/10 text-[#F8FAFC] h-11 focus:border-[#48CCCD] ${errors.name ? 'border-red-500' : ''}`}
                        data-testid="repair-input-name"
                      />
                      {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="07700 000 000"
                        className={`bg-[#16202A] border-white/10 text-[#F8FAFC] h-11 focus:border-[#48CCCD] ${errors.phone ? 'border-red-500' : ''}`}
                        data-testid="repair-input-phone"
                      />
                      {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
                    </div>
                    
                    <div className="sm:col-span-2">
                      <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                        className={`bg-[#16202A] border-white/10 text-[#F8FAFC] h-11 focus:border-[#48CCCD] ${errors.email ? 'border-red-500' : ''}`}
                        data-testid="repair-input-email"
                      />
                      {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                    </div>
                  </div>
                </div>
                
                {/* Vehicle Details - Optional */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Car size={16} className="text-[#48CCCD]" />
                    <span className="text-[10px] uppercase tracking-[0.15em] text-[#48CCCD] font-sans font-medium">
                      Vehicle Details <span className="text-[#94A3B8]">(optional)</span>
                    </span>
                  </div>
                  
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                      Vehicle Information
                    </label>
                    <Input
                      type="text"
                      name="vehicle_details"
                      value={formData.vehicle_details}
                      onChange={handleInputChange}
                      placeholder="Reg, make/model if relevant"
                      className="bg-[#16202A] border-white/10 text-[#F8FAFC] h-11 focus:border-[#48CCCD]"
                      data-testid="repair-input-vehicle"
                    />
                  </div>
                </div>
                
                {/* Appointment Details */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText size={16} className="text-[#48CCCD]" />
                    <span className="text-[10px] uppercase tracking-[0.15em] text-[#48CCCD] font-sans font-medium">
                      Appointment Details
                    </span>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                        What is this appointment regarding? <span className="text-[#94A3B8]">(optional)</span>
                      </label>
                      <Input
                        type="text"
                        name="issue_summary"
                        value={formData.issue_summary}
                        onChange={handleInputChange}
                        placeholder="e.g. Test drive, view a car, service booking"
                        className="bg-[#16202A] border-white/10 text-[#F8FAFC] h-11 focus:border-[#48CCCD]"
                        data-testid="repair-input-issue"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                        Additional Notes <span className="text-[#94A3B8]">(optional)</span>
                      </label>
                      <Textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        placeholder="Any other details or requirements..."
                        rows={3}
                        className="bg-[#16202A] border-white/10 text-[#F8FAFC] focus:border-[#48CCCD] resize-none"
                        data-testid="repair-input-notes"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Preferred Date/Time */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar size={16} className="text-[#48CCCD]" />
                    <span className="text-[10px] uppercase tracking-[0.15em] text-[#48CCCD] font-sans font-medium">
                      Availability
                    </span>
                  </div>
                  
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                      Preferred Date/Time <span className="text-[#94A3B8]">(optional)</span>
                    </label>
                    <Input
                      type="text"
                      name="preferred_datetime"
                      value={formData.preferred_datetime}
                      onChange={handleInputChange}
                      placeholder="e.g. Weekday mornings, or Friday 2pm"
                      className="bg-[#16202A] border-white/10 text-[#F8FAFC] h-11 focus:border-[#48CCCD]"
                      data-testid="repair-input-datetime"
                    />
                  </div>
                </div>
                
                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-12 bg-[#48CCCD] text-black font-bold uppercase tracking-widest text-xs hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="repair-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Calendar size={18} className="mr-2" />
                      Request Appointment
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-[#94A3B8] text-center mt-4">
                  We&apos;ll contact you within 24 hours to confirm your appointment.
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RepairsPage;
